/**
 * Screencast File Encoder
 *
 * Pipes JPEG frames from Playwright's page.screencast API into ffmpeg,
 * producing an H.264 MP4 file with wall-clock-accurate timing.
 *
 * Unlike Playwright's built-in recordVideo (which uses Chrome's compositor
 * timestamps and drifts from wall clock), this encoder uses performance.now()
 * (monotonic clock) at frame receipt time, ensuring the output duration
 * matches real elapsed time exactly.
 *
 * Architecture:
 *   page.screencast onFrame (JPEG) -> pushFrame() -> ffmpeg stdin (image2pipe)
 *   ffmpeg encodes H.264 baseline + faststart -> output.mp4
 *
 * Lifecycle (spawn, stderr capture, close/error/stdin-error, stop-with-timeout)
 * is delegated to FfmpegProcess; this class owns frame queuing, gap-fill,
 * and the wall-clock-accurate stop result.
 *
 * @module screencast-file-encoder
 */

import path from 'node:path';
import { performance } from 'perf_hooks';
import { FfmpegProcess } from './ffmpeg-process.js';
import type { BrowserLogger } from './logger.js';
import { consoleLogger } from './logger.js';
import {
  isScreencastTraceEnabled,
  traceDigest,
  traceScreencast,
} from './screencast-trace.js';

/** Maximum gap-fill frames per pushFrame call (5 minutes at any fps). */
const MAX_GAP_FILL_SECONDS = 300;
const STOP_TIMEOUT_MS = 30_000;

export interface EncoderStopResult {
  durationMs: number;
  receivedFrameCount: number;
  writtenFrameCount: number;
  /**
   * True if ffmpeg exited unexpectedly or a stdin write failed during the
   * recording. The output MP4 may be truncated/invalid; callers should treat
   * the file as suspect.
   */
  encoderFailed: boolean;
  /** Wall-clock ms when start() was called (ffmpeg spawn initiated). */
  encoderCreatedAtMs: number | null;
  /** Wall-clock ms of the first frame accepted by pushFrame (== startedAtMs). */
  firstFrameWallClockMs: number | null;
  /** Frames dropped before ffmpeg stdin became writable (front-gap diagnostic). */
  framesDroppedBeforeReady: number;
  /** Largest inter-frame gap (ms) across accepted frames — surfaces stalls. */
  maxInterFrameGapMs: number;
}

export interface ScreencastFileEncoderOptions {
  /** Output file path (must end in .mp4) */
  outputPath: string;
  /** Video dimensions */
  size: { width: number; height: number };
  /** Target framerate. Default: 10 (sufficient for screen recording) */
  fps?: number;
  /** Logger instance */
  logger?: BrowserLogger;
}

/**
 * Does a captured frame differ from the encoder canvas enough to be worth
 * reporting?
 *
 * The canvas is fixed when the encoder starts and cannot change mid-stream, so
 * any difference is absorbed by the pad/crop filter — which is how a 140-row band
 * of fill colour reached every recorded video with nothing in the logs to explain
 * it. Callers report a `true` so the divergence names itself and both sizes.
 *
 * A ≤1px difference is not reported: the canvas rounds down to even dimensions
 * for yuv420p, so an odd viewport is expected to be off by one.
 */
export function isReportableCanvasMismatch(
  canvas: { width: number; height: number },
  frame: { width: number; height: number }
): boolean {
  return Math.abs(frame.width - canvas.width) > 1 || Math.abs(frame.height - canvas.height) > 1;
}

/**
 * Build the ffmpeg argv for a screencast encode.
 *
 * Extracted from `start()` so the filter graph is unit-testable — the padding
 * below silently changed what every recorded video looks like for weeks with no
 * test covering it.
 */
export function buildFfmpegArgs(opts: {
  size: { width: number; height: number };
  fps: number;
  outputPath: string;
}): string[] {
  const { width, height } = opts.size;
  const { fps, outputPath } = opts;

  return [
    '-y',
    // Input: JPEG frames piped via stdin
    '-f', 'image2pipe',
    '-framerate', String(fps),
    '-i', 'pipe:0',
    // H.264 encoding — matches existing transcode settings
    '-c:v', 'libx264',
    '-preset', 'ultrafast',
    '-crf', '28',
    '-profile:v', 'baseline',
    '-level', '3.0',
    '-pix_fmt', 'yuv420p',
    // One independently decodable keyframe per second. Per-test extraction
    // can then stream-copy at test boundaries instead of re-encoding an
    // entire failure session.
    '-g', String(fps),
    '-keyint_min', String(fps),
    '-sc_threshold', '0',
    // Normalize every frame to a fixed canvas so the output has stable
    // dimensions (players and the trace viewer assume they don't change
    // mid-file) and even width/height, which libx264 + yuv420p require.
    //
    // The pad is NOT the ~1px even-dimension rounding its old comment claimed.
    // `size` is the *configured* viewport (playwright-client.ts, from
    // lastConnectOptions.viewport ?? DEFAULT_VIEWPORT = 1280x800), while CDP
    // screencast frames arrive shorter than that in practice — measured at
    // 1280x660 on real dev runs, so 140 rows (17.5% of the frame) get padded.
    //
    // The fill was `gray` (#808080), which against a typical white page reads
    // as a rendering defect in every customer-facing video. `white` blends
    // instead. This is deliberately cosmetic: the real bug is that the frames
    // do not match the configured viewport, which is tracked separately — fix
    // that and this pad goes back to being the ~1px no-op it was meant to be.
    '-vf', `pad=${width}:${height}:0:0:white,crop=${width}:${height}:0:0`,
    '-an',
    // Output: MP4 with moov atom at start for instant seeking from S3
    '-f', 'mp4',
    '-movflags', '+faststart',
    outputPath,
  ];
}

/**
 * Wall-clock-accurate video encoder for Playwright screencast frames.
 *
 * Accepts JPEG frames and writes them to an ffmpeg process that produces
 * H.264 MP4 with +faststart (instant seeking from S3). Fills idle gaps
 * with duplicate frames based on monotonic clock deltas, ensuring the output
 * duration matches real wall-clock time.
 */
export class ScreencastFileEncoder {
  private proc: FfmpegProcess | null = null;
  private readonly outputPath: string;
  private readonly size: { width: number; height: number };
  private readonly fps: number;
  private readonly logger: BrowserLogger;
  private readonly maxGapFillFrames: number;

  private _started = false;
  private _stopped = false;
  /** Monotonic timestamp (ms) of first frame via performance.now() */
  private firstFrameMonotonic: number | null = null;
  /** Wall-clock Date.now() captured at first frame — used for startedAtMs */
  private firstFrameWallClock: number | null = null;
  private lastFrameNumber = -1;
  private lastFrameBuffer: Buffer | null = null;
  private receivedFrameCount = 0;
  private writtenFrameCount = 0;
  private lastWritePromise: Promise<void> = Promise.resolve();
  private frameQueue: Buffer[] = [];
  private _writeError = false;
  private _encoderFailed = false;
  /** Wall-clock ms when start() was called. */
  private encoderCreatedAtMs: number | null = null;
  /** Frames dropped because ffmpeg stdin wasn't writable yet. */
  private framesDroppedBeforeReady = 0;
  /** Largest inter-frame gap on accepted frames (ms). */
  private maxInterFrameGapMs = 0;
  /** Monotonic ts of last accepted frame. */
  private lastAcceptedMonotonic: number | null = null;

  constructor(options: ScreencastFileEncoderOptions) {
    this.outputPath = options.outputPath;
    this.size = {
      // ffmpeg requires even dimensions
      width: options.size.width & ~1,
      height: options.size.height & ~1,
    };
    this.fps = options.fps ?? 10;
    this.logger = options.logger ?? consoleLogger;
    this.maxGapFillFrames = this.fps * MAX_GAP_FILL_SECONDS;
  }

  get outputFilePath(): string {
    return this.outputPath;
  }

  get outputDir(): string {
    return path.dirname(this.outputPath);
  }

  /** Wall-clock timestamp (ms) when the first frame was received */
  get startedAtMs(): number | null {
    return this.firstFrameWallClock;
  }

  /**
   * Start the ffmpeg process. Call this before pushing frames.
   */
  start(): void {
    if (this._started) return;
    this._started = true;
    this.encoderCreatedAtMs = Date.now();

    const args = buildFfmpegArgs({
      size: this.size,
      fps: this.fps,
      outputPath: this.outputPath,
    });

    this.logger.info('[ScreencastFileEncoder] Starting ffmpeg', {
      fps: this.fps,
      size: this.size,
      outputPath: this.outputPath,
    });

    this.proc = new FfmpegProcess({
      args,
      logTag: '[ScreencastFileEncoder]',
      logger: this.logger,
      stopTimeoutMs: STOP_TIMEOUT_MS,
      onUnexpectedExit: () => {
        this._encoderFailed = true;
      },
    });
    this.proc.start();
  }

  /**
   * Push a JPEG frame into the encoder.
   *
   * Uses performance.now() (monotonic clock) for frame timing and fills
   * gaps with duplicate frames so the output duration matches wall-clock time.
   */
  pushFrame(jpegBuffer: Buffer): void {
    if (!this._started || this._stopped || this._writeError) {
      return;
    }
    if (!this.proc?.stdinWritable) {
      // ffmpeg is still spawning — count drops so we can attribute the front-gap
      // between encoder creation and first accepted frame to spawn-warmup vs.
      // CDP first-frame delivery.
      this.framesDroppedBeforeReady++;
      return;
    }

    const now = performance.now();
    if (!this.firstFrameMonotonic) {
      this.firstFrameMonotonic = now;
      this.firstFrameWallClock = Date.now();
    } else if (this.lastAcceptedMonotonic !== null) {
      const gap = now - this.lastAcceptedMonotonic;
      if (gap > this.maxInterFrameGapMs) this.maxInterFrameGapMs = gap;
    }
    this.lastAcceptedMonotonic = now;

    const elapsed = Math.max(0, now - this.firstFrameMonotonic) / 1000; // seconds, clamped non-negative
    const frameNumber = Math.floor(elapsed * this.fps);

    this.receivedFrameCount++;

    // Phase-0 diagnostics. The bucket decision below is the pipeline's SECOND
    // discard point (the bus throttle is the first): when this push lands in the
    // same `frameNumber` slot as the previous one, `lastFrameBuffer` is replaced
    // just below and the earlier frame never reaches ffmpeg at all. That biases
    // each slot toward whichever accepted frame arrived LAST — the reason a
    // short-lived pre-action state can be accepted by the bus and still be
    // absent from the mp4.
    if (isScreencastTraceEnabled()) {
      const advancesBucket = this.lastFrameBuffer !== null && frameNumber > this.lastFrameNumber;
      traceScreencast({
        kind: 'encoder_push',
        frameNumber,
        previousFrameNumber: this.lastFrameNumber,
        gapFillCount: advancesBucket
          ? Math.min(frameNumber - this.lastFrameNumber, this.maxGapFillFrames)
          : 0,
        overwrotePreviousFrame: this.lastFrameBuffer !== null && frameNumber === this.lastFrameNumber,
        digest: traceDigest(jpegBuffer),
        bytes: jpegBuffer.byteLength,
      });
    }

    // Fill gap with duplicate frames (same approach as Playwright's videoRecorder.js:147-150).
    // These fill frames are essential for wall-clock fidelity — they ensure idle periods
    // produce the correct video duration.
    if (this.lastFrameBuffer && frameNumber > this.lastFrameNumber) {
      let repeatCount = frameNumber - this.lastFrameNumber;
      if (repeatCount > this.maxGapFillFrames) {
        this.logger.warn('[ScreencastFileEncoder] Gap fill capped — possible clock anomaly', {
          repeatCount,
          maxGapFillFrames: this.maxGapFillFrames,
          elapsedSeconds: elapsed,
        });
        repeatCount = this.maxGapFillFrames;
      }
      for (let i = 0; i < repeatCount; i++) {
        this.frameQueue.push(this.lastFrameBuffer);
      }
      this.lastWritePromise = this.lastWritePromise.then(() => this._sendFrames());
    }

    this.lastFrameBuffer = jpegBuffer;
    this.lastFrameNumber = frameNumber;
  }

  /**
   * Stop the encoder gracefully.
   *
   * Writes final duplicate frames to cover elapsed wall-clock time,
   * closes stdin to signal EOF, then waits for ffmpeg to flush and
   * write the faststart moov atom.
   */
  async stop(): Promise<EncoderStopResult> {
    const baseDiagnostics = {
      receivedFrameCount: this.receivedFrameCount,
      writtenFrameCount: this.writtenFrameCount,
      encoderFailed: this._encoderFailed,
      encoderCreatedAtMs: this.encoderCreatedAtMs,
      firstFrameWallClockMs: this.firstFrameWallClock,
      framesDroppedBeforeReady: this.framesDroppedBeforeReady,
      maxInterFrameGapMs: Math.round(this.maxInterFrameGapMs),
    };
    if (this._stopped) {
      return { durationMs: 0, ...baseDiagnostics };
    }
    this._stopped = true;

    const durationMs = this.firstFrameMonotonic
      ? Math.max(0, performance.now() - this.firstFrameMonotonic)
      : 0;

    if (!this.proc) {
      return { durationMs, ...baseDiagnostics };
    }

    // Write final frames to cover the full wall-clock duration
    if (this.lastFrameBuffer && this.firstFrameMonotonic) {
      const totalElapsed = durationMs / 1000;
      const finalFrameNumber = Math.floor(totalElapsed * this.fps);
      if (finalFrameNumber > this.lastFrameNumber) {
        const remaining = finalFrameNumber - this.lastFrameNumber;
        for (let i = 0; i < remaining; i++) {
          this.frameQueue.push(this.lastFrameBuffer);
        }
        await this.lastWritePromise.then(() => this._sendFrames());
      }
    }

    // Wait for pending writes
    await this.lastWritePromise;

    // Delegate close-with-timeout to FfmpegProcess. It closes stdin internally,
    // waits up to STOP_TIMEOUT_MS, then SIGKILLs.
    const proc = this.proc;
    this.proc = null;
    await proc.stop();
    return { durationMs, ...baseDiagnostics };
  }

  /**
   * Kill the encoder immediately without waiting for flush.
   */
  kill(): void {
    this._stopped = true;
    if (this.proc) {
      this.proc.kill();
      this.proc = null;
    }
  }

  private async _sendFrames(): Promise<void> {
    // Swap-and-iterate pattern: O(n) instead of O(n²) from shift() in a loop
    const frames = this.frameQueue;
    this.frameQueue = [];
    for (const frame of frames) {
      if (this._writeError) break;
      await this._sendFrame(frame);
    }
  }

  private async _sendFrame(frame: Buffer): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.proc?.stdinWritable || this._writeError) {
        resolve();
        return;
      }
      this.proc.writeFrame(frame, (error) => {
        if (error) {
          this._writeError = true;
          this._encoderFailed = true;
          this.logger.warn('[ScreencastFileEncoder] Write failed, stopping further writes', {
            error: error.message,
            writtenFrameCount: this.writtenFrameCount,
          });
        } else {
          this.writtenFrameCount++;
        }
        resolve();
      });
    });
  }
}
